/**
 * File: useADRActiveTab.ts
 * Description: Custom hook to manage ADR active tab state
 * Author: GitHub Copilot
 * Created: 2025-01-XX
 * Last Modified: 2025-01-XX
 */

import { useEffect, useRef } from 'react';

interface UseADRActiveTabProps {
  isLoadingDefinitions: boolean;
  definitionsCount: number;
  onTabChange: (tab: 'suggestions' | 'definitions') => void;
}

/**
 * Hook to manage active tab based on definitions state.
 * Switches to the definitions tab only once, on the initial data load,
 * when definitions already exist. Subsequent changes (e.g. accepting a
 * suggestion) do not cause a tab switch.
 */
export const useADRActiveTab = ({
  isLoadingDefinitions,
  definitionsCount,
  onTabChange,
}: UseADRActiveTabProps): void => {
  const hasInitialisedRef = useRef(false);

  useEffect(() => {
    if (hasInitialisedRef.current) {return;}
    if (!isLoadingDefinitions && definitionsCount > 0) {
      hasInitialisedRef.current = true;
      onTabChange('definitions');
    }
  }, [isLoadingDefinitions, definitionsCount, onTabChange]);
};
